import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import {
  Close,
  Download,
  Edit,
  OpenInNew,
  Description,
} from '@mui/icons-material';
import mammoth from 'mammoth';
import PDFViewer, { PdfAnnotation } from './PDFViewer';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { getAuthToken } from '../../lib/authToken';

export interface PreviewDocument {
  id: string;
  title: string;
  originalName?: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  metadata?: Record<string, unknown>;
}

interface DocumentPreviewDialogProps {
  open: boolean;
  document: PreviewDocument | null;
  onClose: () => void;
  onEditProperties?: (document: PreviewDocument) => void;
  /** Open rich-text / body editor (DOCX, text, HTML) */
  onEditContent?: (document: PreviewDocument) => void;
  onDownload?: (document: PreviewDocument) => void;
}

const isContentEditable = (mimeType?: string, fileName?: string): boolean => {
  const mime = (mimeType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  if (mime.includes('wordprocessingml') || name.endsWith('.docx')) return true;
  if (mime.startsWith('text/') || /\.(txt|csv|md|json|xml|html?)$/.test(name)) return true;
  return false;
};

type PreviewKind = 'pdf' | 'image' | 'docx' | 'text' | 'unsupported';

const detectKind = (mimeType?: string, fileName?: string): PreviewKind => {
  const mime = (mimeType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(name)) return 'image';
  if (
    mime.includes('wordprocessingml') ||
    mime === 'application/msword' ||
    name.endsWith('.docx') ||
    name.endsWith('.doc')
  ) {
    // mammoth handles docx; .doc gets unsupported fallback
    if (name.endsWith('.doc') && !name.endsWith('.docx')) return 'unsupported';
    return 'docx';
  }
  if (mime.startsWith('text/') || /\.(txt|csv|md|json|xml|html?)$/.test(name)) return 'text';
  return 'unsupported';
};

/**
 * Practice-aligned View: show the actual file content.
 * Metadata belongs in Edit Properties — not behind the View action.
 */
const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  document,
  onClose,
  onEditProperties,
  onEditContent,
  onDownload,
}) => {
  const canEditBody = Boolean(
    document && onEditContent && isContentEditable(document.mimeType, document.originalName || document.fileName)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [htmlPreview, setHtmlPreview] = useState<string | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [kind, setKind] = useState<PreviewKind>('unsupported');

  const cleanup = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setHtmlPreview(null);
    setTextPreview(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open || !document?.id) {
      cleanup();
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setHtmlPreview(null);
      setTextPreview(null);

      const nextKind = detectKind(document.mimeType, document.originalName || document.fileName);
      setKind(nextKind);

      try {
        const token = getAuthToken();
        if (!token) throw new Error('Authentication required');

        const response = await fetch(`/api/documents/${document.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(await getApiErrorMessage(response, 'Unable to open document preview.'));
        }

        const blob = await response.blob();
        if (cancelled) return;

        if (nextKind === 'docx') {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (!cancelled) {
            setHtmlPreview(result.value || '<p><em>(Empty document)</em></p>');
          }
        } else if (nextKind === 'text') {
          const text = await blob.text();
          if (!cancelled) setTextPreview(text);
        } else {
          const url = URL.createObjectURL(blob);
          if (!cancelled) setBlobUrl(url);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Document preview error:', err);
          setError(toUserErrorMessage(err, 'Unable to open document preview.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [open, document?.id, document?.mimeType, document?.originalName, document?.fileName, cleanup]);

  const handleDownload = () => {
    if (!document) return;
    if (onDownload) {
      onDownload(document);
      return;
    }
    // Fallback: use blob URL or fetch download
    window.open(`/api/documents/${document.id}/download`, '_blank');
  };

  const handleOpenNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
      return;
    }
    if (document) {
      handleDownload();
    }
  };

  // PDF viewer is its own full dialog — use it when ready
  if (open && document && kind === 'pdf' && blobUrl && !loading && !error) {
    return (
      <PDFViewer
        open={open}
        url={blobUrl}
        title={document.title || document.originalName || 'Document'}
        onClose={onClose}
        onDownload={handleDownload}
        documentId={document.id}
        annotationsEnabled
        initialAnnotations={
          Array.isArray(document.metadata?.annotations)
            ? (document.metadata!.annotations as PdfAnnotation[])
            : []
        }
        existingMetadata={document.metadata}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: 1100,
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Description color="primary" />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {document?.title || 'Document'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {document?.originalName || document?.fileName}
          </Typography>
        </Box>
        {document?.mimeType && (
          <Chip size="small" label={kind === 'unsupported' ? document.mimeType : kind.toUpperCase()} />
        )}
        {canEditBody && (
          <Tooltip title="Edit document content">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => document && onEditContent?.(document)}
              >
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Edit properties (title, tags, category)">
          <span>
            <IconButton
              size="small"
              disabled={!document || !onEditProperties}
              onClick={() => document && onEditProperties?.(document)}
            >
              <Description />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton size="small" onClick={handleDownload} disabled={!document}>
            <Download />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton size="small" onClick={handleOpenNewTab} disabled={!blobUrl && kind !== 'unsupported'}>
            <OpenInNew />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.100', display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 6 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Opening document…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleDownload}>
                  Download instead
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && kind === 'image' && blobUrl && (
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', p: 2 }}>
            <Box
              component="img"
              src={blobUrl}
              alt={document?.title || 'Document'}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: 2 }}
            />
          </Box>
        )}

        {!loading && !error && kind === 'docx' && htmlPreview && (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, md: 4 },
              bgcolor: 'background.paper',
              '& img': { maxWidth: '100%' },
              '& p': { mb: 1.5 },
              '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
              '& td, & th': { border: '1px solid', borderColor: 'divider', p: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlPreview) }}
          />
        )}

        {!loading && !error && kind === 'text' && textPreview !== null && (
          <Box
            component="pre"
            sx={{
              flex: 1,
              m: 0,
              p: 3,
              overflow: 'auto',
              bgcolor: 'background.paper',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {textPreview}
          </Box>
        )}

        {!loading && !error && kind === 'pdf' && !blobUrl && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Preparing PDF viewer…</Alert>
          </Box>
        )}

        {!loading && !error && kind === 'unsupported' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              In-browser preview is not available for this file type
              {document?.mimeType ? ` (${document.mimeType})` : ''}.
              Download the file or open it in the associated application.
            </Alert>
            <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>
              Download to view
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {canEditBody && document && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => onEditContent?.(document)}
          >
            Edit document
          </Button>
        )}
        {onEditProperties && document && (
          <Button startIcon={<Description />} onClick={() => onEditProperties(document)}>
            Edit properties
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
